import { useMutation } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'

import { Alert, Badge, Button, Card, Field, Input, useToast } from '../../components/ui'
import { ApiError, api } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { datetime } from '../../lib/format'
import type { User } from '../../lib/types'

export function Settings() {
  const { user, setUser, logout } = useAuth()
  const { push: toast } = useToast()

  const [profile, setProfile] = useState({
    fullName: user?.fullName ?? '',
    company: user?.company ?? '',
    companyTaxId: user?.companyTaxId ?? '',
    phone: user?.phone ?? '',
  })

  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '' })

  const saveProfile = useMutation({
    mutationFn: () => api.patch<User>('/account/profile', profile),
    onSuccess: (updated) => {
      setUser(updated)
      toast('Профіль збережено', 'success')
    },
    onError: (err) =>
      toast(err instanceof ApiError ? err.message : 'Не вдалося зберегти', 'danger'),
  })

  const changePassword = useMutation({
    mutationFn: () => api.post('/auth/change-password', passwords),
    onSuccess: async () => {
      toast('Пароль змінено. Увійдіть заново.', 'success')
      setPasswords({ currentPassword: '', newPassword: '' })
      await logout()
    },
    onError: (err) =>
      toast(err instanceof ApiError ? err.message : 'Не вдалося змінити пароль', 'danger'),
  })

  if (!user) return null

  const submitProfile = (e: FormEvent) => {
    e.preventDefault()
    saveProfile.mutate()
  }

  const submitPassword = (e: FormEvent) => {
    e.preventDefault()
    changePassword.mutate()
  }

  return (
    <div className="max-w-2xl space-y-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">Налаштування</h1>
      </header>

      <Card title="Обліковий запис">
        <dl className="space-y-3 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500 dark:text-slate-400">Email</dt>
            <dd className="flex items-center gap-2 font-medium">
              {user.email}
              {user.emailVerified ? (
                <Badge tone="success">підтверджено</Badge>
              ) : (
                <Badge tone="warning">не підтверджено</Badge>
              )}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500 dark:text-slate-400">Роль</dt>
            <dd className="font-medium">{user.role === 'admin' ? 'Адміністратор' : 'Користувач'}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500 dark:text-slate-400">Зареєстровано</dt>
            <dd className="font-medium">{datetime(user.createdAt)}</dd>
          </div>
        </dl>
      </Card>

      <Card title="Профіль" description="Ці дані підставляються у генератор за замовчуванням">
        <form onSubmit={submitProfile} className="grid gap-4 sm:grid-cols-2">
          <Field label="Імʼя">
            {(id) => (
              <Input
                id={id}
                value={profile.fullName}
                onChange={(e) => setProfile((p) => ({ ...p, fullName: e.target.value }))}
              />
            )}
          </Field>
          <Field label="Телефон">
            {(id) => (
              <Input
                id={id}
                type="tel"
                value={profile.phone}
                onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))}
                placeholder="+380..."
              />
            )}
          </Field>
          <Field label="Компанія">
            {(id) => (
              <Input
                id={id}
                value={profile.company}
                onChange={(e) => setProfile((p) => ({ ...p, company: e.target.value }))}
              />
            )}
          </Field>
          <Field label="ЄДРПОУ / РНОКПП" hint="8 цифр для ЮО, 10 — для ФОП">
            {(id) => (
              <Input
                id={id}
                inputMode="numeric"
                maxLength={10}
                value={profile.companyTaxId}
                onChange={(e) =>
                  setProfile((p) => ({ ...p, companyTaxId: e.target.value.replace(/\D/g, '') }))
                }
              />
            )}
          </Field>
          <div className="sm:col-span-2">
            <Button type="submit" loading={saveProfile.isPending}>
              Зберегти
            </Button>
          </div>
        </form>
      </Card>

      <Card title="Пароль" description="Зміна пароля завершує всі активні сесії">
        <form onSubmit={submitPassword} className="space-y-4">
          <Field label="Поточний пароль" required>
            {(id) => (
              <Input
                id={id}
                type="password"
                autoComplete="current-password"
                required
                value={passwords.currentPassword}
                onChange={(e) =>
                  setPasswords((p) => ({ ...p, currentPassword: e.target.value }))
                }
              />
            )}
          </Field>
          <Field label="Новий пароль" required hint="Мінімум 10 символів, літери та цифри">
            {(id) => (
              <Input
                id={id}
                type="password"
                autoComplete="new-password"
                required
                minLength={10}
                value={passwords.newPassword}
                onChange={(e) => setPasswords((p) => ({ ...p, newPassword: e.target.value }))}
              />
            )}
          </Field>
          <Button type="submit" loading={changePassword.isPending}>
            Змінити пароль
          </Button>
        </form>
      </Card>

      <Alert tone="danger" title="Видалення акаунта">
        Щоб видалити акаунт і всі повʼязані дані, напишіть на{' '}
        <a href="mailto:api@qr24.app" className="font-semibold underline">
          api@qr24.app
        </a>
        . Ми видалимо їх протягом 30 днів згідно з політикою конфіденційності.
      </Alert>
    </div>
  )
}
